<template>
  <div>
    <v-dialog v-model="showDialog" max-width="1000">
      <v-card>
        <v-btn icon style="position: absolute; top: 0; right: 0; float: right" @click="showDialog = false"
          ><v-icon small icon>mdi-close</v-icon></v-btn
        >
        <v-container fluid>
          <v-row style="min-height: 350px">
            <v-col cols="3">
              <v-list>
                <v-list-item-group v-model="selectedPage">
                  <v-list-item v-for="type in taskTypes" :key="type" :value="type">
                    <v-list-item-content>
                      <v-list-item-title
                        :class="selectedPage === type ? 'font-weight-bold' : ''"
                        style="text-transform: capitalize; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
                      >
                        {{ type }}
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-col>
            <v-divider vertical inset class="mb-2" />
            <v-col cols="9">
              <v-card-title class="mb-0" style="text-transform: capitalize">{{ selectedPage }}</v-card-title>
              <v-row class="mb-0 pb-0">
                <v-col cols="10">
                  <v-text-field
                    v-if="accountCode"
                    ref="task"
                    class="mt-0 ml-4 pt-0"
                    clearable
                    label="Add new task"
                    v-model="newTask"
                    :rules="[rules.lengthRule]"
                    lazy-rules
                    hide-details="auto"
                  />
                  <v-text-field v-else disabled class="mt-0 ml-4 pt-0" label="Must be in an account to add task" />
                </v-col>
                <v-col class="mb-0" cols="2">
                  <toolbar-button size="large" v-if="newTask" :click-method="addTask" color="primary" icon="mdi-plus" label="add task" />
                </v-col>
              </v-row>
              <v-row class="ml-1 mt-0 mb-2" v-if="newTask">
                <v-menu
                  v-model="tagMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primary" text small v-bind="attrs" v-on="on">
                      <v-icon>mdi-account-plus</v-icon>
                      <span class="ml-1 mt-1" style="text-transform: none">{{
                        selectedUser ? getTextUserByValue(selectedUser) : 'Assign'
                      }}</span>
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-text>
                      <v-select :items="taggableUsers" label="Assign user" v-model="selectedUser" @input="tagMenu = false"></v-select>
                    </v-card-text>
                  </v-card>
                </v-menu>

                <v-btn small class="ml-0" v-if="selectedUser" icon @click="selectedUser = null">
                  <v-icon>mdi-close</v-icon>
                </v-btn>

                <v-menu v-model="menu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition" offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primary" small text v-bind="attrs" v-on="on">
                      <v-icon>mdi-calendar</v-icon>
                      <span class="ml-1 mt-1" style="text-transform: none">{{
                        dueDate ? isoDateToCommonDate(dueDate) : 'Set due date'
                      }}</span>
                    </v-btn>
                  </template>
                  <v-date-picker v-model="dueDate" @input="menu = false" :allowed-dates="allowedDates"></v-date-picker>
                </v-menu>
                <v-btn small class="ml-0" v-if="dueDate" icon @click="dueDate = null">
                  <v-icon>mdi-close</v-icon>
                </v-btn>

                <v-tooltip bottom nudge-top="20px" z-index="9999">
                  <template v-slot:activator="{ on, attrs }">
                    <div v-on="on" v-bind="attrs">
                      <v-switch color="primary" class="mt-0 ml-1" v-model="linkToPage" v-on="on" v-bind="attrs">
                        <template v-slot:label>
                          <span class="primary--text caption" style="font-size: 12px; font-weight: 500">Link to page</span>
                        </template>
                      </v-switch>
                    </div>
                  </template>
                  <span>Categorizes & links task to current page</span>
                </v-tooltip>
              </v-row>
              <v-row v-if="tasks.length === 0" style="text-align: center; height: 150px" class="align-center justify-center flex-column">
                <div>
                  <v-icon color="darkest" large>mdi-calendar-check</v-icon>
                </div>
                <div class="text-h6 darkest--text">All caught up!</div>
              </v-row>

              <v-list v-else dense>
                <v-list-item v-for="task in tasks" :key="task.id" class="list-item">
                  <v-checkbox
                    :disabled="!task.isTagged"
                    :class="{ 'disabled-checkbox': !task.isTagged }"
                    v-model="task.completed"
                    @change="markComplete(task)"
                  ></v-checkbox>
                  <v-list-item-content :class="task.completed ? 'text-decoration-line-through' : ''">
                    <v-list-item-title class="wrap-text">
                      {{ task.details }}
                      <v-chip :class="getColorClass(task.page)" v-if="selectedPage === 'All Tasks' && task.page" x-small class="ml-2">{{
                        task.page
                      }}</v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="task.dueEpoch || task.tagged">
                      <span v-if="task.tagged" class="icon-align mr-4">
                        <v-icon small>mdi-account</v-icon> {{ getTextUserByValue(task.tagged) }}
                      </span>

                      <span v-if="task.dueEpoch" class="icon-align mr-4">
                        <v-icon small>mdi-calendar</v-icon><span :class="dueClass(task.dueEpoch)">{{ formatDueEpoch(task.dueEpoch) }}</span>
                      </span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <div class="actions">
                    <v-icon v-if="task.link" :disabled="isOnLink(task.link)" @click="$router.push(task.link)">mdi-link</v-icon>
                    <v-icon v-if="isEditable(task)" class="mr-2" @click="editTask(task)">mdi-pencil</v-icon>
                    <v-icon v-if="isEditable(task)" @click="deleteTask(task.id)">mdi-delete</v-icon>
                  </div>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
          <v-checkbox
            hide-details
            dense
            style="position: absolute; bottom: 10px; left: 10px"
            v-model="showOnlyUserTasks"
            label="Show only my tasks"
          >
            <template v-slot:label> <span class="text-body-2">Show only my tasks</span> </template>
          </v-checkbox>
        </v-container>
      </v-card>
      <user-task-edit-dialog :tagged-options="taggableUsers" :task="editingTask" :show.sync="editDialog" @save="updateTask" />
    </v-dialog>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import ToolbarButton from '@/components/shared/ToolbarButton.vue'
import { addDays, format, isEqual, isFuture, isPast, isToday, parseISO } from 'date-fns'
import uuidHelper from '@/mixins/uuid-helper'
import UserTaskEditDialog from '@/components/tasks/UserTaskEditDialog.vue'
import dateHelper from '@/mixins/date-helper'
import { addUserTask, deleteUserTask, updateUserTask } from '@/services/tasks-service'
export default {
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    showOnlyUser: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [uuidHelper, dateHelper],
  components: { UserTaskEditDialog, ToolbarButton },
  name: 'UserTaskTasks',

  data() {
    return {
      linkToPage: true,
      showOnlyUserTasks: this.showOnlyUser,
      selectedPage: 'All Tasks',
      newTask: '',
      selectedUser: null,
      dueDate: null,
      menu: false,
      tagMenu: false,
      typeColors: {},
      colorClasses: ['darkest', 'primary', 'secondary', 'success', 'warning', 'accent', 'info'],
      currentColorIndex: 0,
      taskActionCanceled: false,
      timeoutId: null,
      editDialog: false,
      editingTask: null,
      rules: {
        lengthRule: value => value == null || value.length <= 200 || 'Must be less then 250 characters',
      },
    }
  },

  watch: {
    showOnlyUserTasks: {
      handler() {
        this.selectedPage = 'All Tasks'
      },
      immediate: true,
    },
    selectedPage(newVal) {
      if (newVal === '' || newVal == null) {
        this.selectedPage = 'All Tasks'
      }
    },
  },

  computed: {
    ...mapGetters({
      getUser: 'getUser',
      getActiveAccount: 'getActiveAccount',
      getAccountTasks: 'getAccountTasks',
    }),
    taskTypes() {
      const types = ['All Tasks']
      let tasks = []
      if (!this.showOnlyUserTasks) {
        tasks = this.getAccountTasks?.all || []
      } else {
        tasks = this.getAccountTasks?.tagged || []
      }
      tasks.map(item => {
        if (item.page && !types.includes(item.page)) {
          types.push(item.page)
        }
      })
      return types
    },
    showDialog: {
      get() {
        return this.dialog
      },
      set(newValue) {
        this.$emit('update:dialog', newValue)
      },
    },
    taggableUsers() {
      const items = []
      const accountUsersMap = this.getActiveAccount.accountUsers || {}
      for (let key of Object.keys(accountUsersMap)) {
        const user = accountUsersMap[key]
        items.push({
          text: `${user.firstName} ${user.lastName}`,
          value: user.username,
        })
      }
      return items
    },
    tasks() {
      let tasks = []
      if (!this.showOnlyUserTasks) {
        tasks = this.getAccountTasks?.all || []
      } else {
        tasks = this.getAccountTasks?.tagged || []
      }
      if (this.selectedPage === 'All Tasks') {
        return tasks
      }
      return tasks.filter(item => item.page === this.selectedPage)
    },
    isPrimaryAccountUser() {
      const username = this.getUser.username
      const accountUsersMap = this.getActiveAccount.accountUsers || {}
      for (let value of Object.values(accountUsersMap)) {
        if (value.role === 'P_ACCT_MGR' && value.username === username) {
          return true
        }
      }
      return false
    },
    accountCode() {
      return this.$route.params.accountId || this.$route.params.accountCode
    },
    endPoint() {
      return `/api/accounts/${this.accountCode}/tasks`
    },
  },
  methods: {
    async addTask() {
      const task = {
        details: this.newTask,
        dueEpoch: this.dueDate ? this.getEpochAtNoon(this.dueDate) : null,
        page: this.linkToPage ? this.pageWithoutParams() : null,
        link: this.linkToPage ? this.$route.path : null,
        tagged: this.selectedUser ? this.selectedUser : this.getUser.username,
        secondaryId: '',
      }
      try {
        this.$loading(true)
        await addUserTask(this.accountCode, task)
        this.resetTask()
        this.$alert('successfully saved task')
      } catch (error) {
        console.error('Failed to save task', error)
        this.$error('Failed to save task')
      } finally {
        this.$loading(false)
      }
    },
    async updateTask(task) {
      try {
        this.$loading(true)
        await updateUserTask(this.accountCode, task)
        this.editDialog = false
        this.$alert('successfully updated task')
      } catch (error) {
        console.error('Failed to update task', error)
        this.$error('Failed to update task')
      } finally {
        this.$loading(false)
      }
    },
    async markComplete(task) {
      if (task.completed === false) {
        this.cancelMarkingTask(task)
        return
      }
      this.taskActionCanceled = false
      this.timeoutId = setTimeout(() => {
        this.markCompleteConfirmed(task)
      }, 5000)
      this.$alert({ show: true, text: 'Marked task as complete', action: () => this.cancelMarkingTask(task) })
    },
    async markCompleteConfirmed(task) {
      try {
        await updateUserTask(this.accountCode, task)
      } catch (error) {
        console.error('Failed complete task', error)
        this.$error('Failed to mark task as complete')
      }
    },
    cancelMarkingTask(task) {
      if (this.timeoutId !== null) {
        clearTimeout(this.timeoutId)
        task.completed = false
        this.taskActionCanceled = true
        this.timeoutId = null
      }
    },
    async deleteTask(task) {
      this.$loading(true)
      try {
        await deleteUserTask(this.accountCode, task)
        this.$alert('successfully deleted task')
      } catch (error) {
        console.error('Failed to delete task', error)
        this.$error('Failed to delete task')
      } finally {
        this.$loading(false)
      }
    },
    getEpochAtNoon(dateStr) {
      const date = parseISO(dateStr)
      date.setHours(12, 0, 0, 0)
      return date.getTime()
    },
    pageWithoutParams() {
      const { matched } = this.$route
      if (!matched || matched.length === 0) {
        return path
      }
      const path = matched[0].path
      const parts = path.split('/')
      // get the segment before the first params that is not accountId to use as category/page
      for (let i = 1; i < parts.length; i++) {
        if (!parts[i].startsWith(':accountId') && parts[i].startsWith(':')) {
          return parts[i - 1]
        }
      }
      return parts[parts.length - 1]
    },

    editTask(task) {
      this.editingTask = task
      this.editDialog = true
    },
    getColorClass(type) {
      if (!this.typeColors[type]) {
        this.typeColors[type] = this.colorClasses[this.currentColorIndex]
        this.currentColorIndex = (this.currentColorIndex + 1) % this.colorClasses.length
      }
      return this.typeColors[type]
    },

    isEditable(task) {
      return task.isOwner || this.isPrimaryAccountUser
    },

    formatDueEpoch(epoch) {
      if (epoch) {
        return format(new Date(epoch), 'MM/dd/yyyy')
      }
      return null
    },

    getTextUserByValue(value) {
      const item = this.taggableUsers.find(item => item.value === value)
      return item ? item.text : value
    },

    resetTask() {
      this.newTask = ''
      this.dueDate = null
      this.selectedUser = null
      this.$refs.task.reset()
    },
    dueClass(epoch) {
      if (epoch) {
        const date = new Date(epoch)
        if (isEqual(date, new Date()) || isPast(date)) {
          return 'error--text'
        } else if (isToday(date)) {
          return 'warning--text'
        }
      }
      return ''
    },
    isOnLink(link) {
      return this.$route.fullPath === link
    },
    allowedDates(date) {
      const compareTo = addDays(new Date(date), 1)
      return isToday(compareTo) || isFuture(compareTo)
    },
  },
}
</script>

<style scoped>
.list-item {
  transition: background-color 0.3s;
}

.list-item:hover {
  background-color: #eeeeee;
}
.list-item .actions {
  visibility: hidden;
}

.list-item:hover .actions {
  visibility: visible;
}

.actions {
  display: flex;
  align-items: center;
}

.actions > .v-icon {
  cursor: pointer;
  margin: 0 8px;
}
.disabled-checkbox {
  opacity: 0.3;
}

.icon-align .v-icon {
  margin-bottom: 3px;
}
.wrap-text {
  white-space: normal;
  overflow-wrap: break-word;
}
</style>
